import { z } from 'zod';
import {
  WebAppCandidateVerdictSchema,
  WebAppDraftStrategySchema,
} from '@terra/shared-schemas-orchestrator';

export { WebAppCandidateVerdictSchema, WebAppDraftStrategySchema };

export const WebAppClusterMemberSchema = z.object({
  id: z.string(),
  originUrl: z.string(),
  apexDomain: z.string(),
  domainId: z.string().nullable(),
  networkServiceId: z.string().nullable(),
  pageTitle: z.string().nullable(),
  faviconHash: z.string().nullable(),
  product: z.string().nullable(),
});

export const WebAppLinkedApplicationSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
  isDraft: z.boolean(),
});

export const WebAppApplicationMatchReasonSchema = z.enum(['URL', 'DOMAIN']);

export const WebAppSuggestedApplicationSchema =
  WebAppLinkedApplicationSchema.extend({
    matchReason: WebAppApplicationMatchReasonSchema,
    matchedHostname: z.string(),
  });

export const WebAppClusterSchema = z.object({
  id: z.string().optional(),
  similarityKey: z.string(),
  canonicalOriginUrl: z.string(),
  apexDomain: z.string(),
  verdict: WebAppCandidateVerdictSchema,
  pageTitle: z.string().nullable(),
  faviconHash: z.string().nullable(),
  product: z.string().nullable(),
  applicationId: z.string().nullable(),
  application: WebAppLinkedApplicationSchema.nullable(),
  suggestedApplication: WebAppSuggestedApplicationSchema.nullable().optional(),
  members: z.array(WebAppClusterMemberSchema),
});

export const ListWebAppClustersResponseSchema = z.object({
  clusters: z.array(WebAppClusterSchema),
  customCount: z.number().int(),
  saasCount: z.number().int(),
});

export const PreviewWebAppDraftsQuerySchema = z.object({
  strategy: WebAppDraftStrategySchema.optional(),
});

export const PreviewWebAppDraftsResponseSchema = z.object({
  strategy: WebAppDraftStrategySchema,
  clusters: z.array(WebAppClusterSchema),
  customCount: z.number().int(),
  saasCount: z.number().int(),
});

export const ConfirmDetectWebAppsLinkSchema = z.object({
  similarityKey: z.string().min(1),
  applicationId: z.string().min(1),
});

export const ConfirmDetectWebAppsBodySchema = z
  .object({
    strategy: WebAppDraftStrategySchema.optional(),
    selectedSimilarityKeys: z.array(z.string().min(1)),
    links: z.array(ConfirmDetectWebAppsLinkSchema).optional(),
    enableAutoCreate: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.links) {
      return;
    }

    const selected = new Set(value.selectedSimilarityKeys);
    const similarityKeys = new Set<string>();
    const applicationIds = new Set<string>();

    for (const link of value.links) {
      if (!selected.has(link.similarityKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Linked clusters must be included in the selection',
          path: ['links'],
        });
      }

      if (similarityKeys.has(link.similarityKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Each cluster can only be linked once',
          path: ['links'],
        });
      }

      if (applicationIds.has(link.applicationId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Each application can only be linked to one cluster',
          path: ['links'],
        });
      }

      similarityKeys.add(link.similarityKey);
      applicationIds.add(link.applicationId);
    }
  });

export const LinkWebAppClusterBodySchema = z.object({
  similarityKey: z.string().min(1),
  applicationId: z.string().min(1),
});

export const LinkWebAppClusterResponseSchema = z.object({
  similarityKey: z.string(),
  applicationId: z.string(),
});

export const CreateWebAppClusterDraftBodySchema = z.object({
  similarityKey: z.string().min(1),
});

export const CreateWebAppClusterDraftResponseSchema =
  LinkWebAppClusterResponseSchema;

export const ConfirmDetectWebAppsResponseSchema = z.object({
  detectWebAppsEnabled: z.boolean(),
  webAppDraftStrategy: WebAppDraftStrategySchema,
  createdApplicationIds: z.array(z.string()),
  linkedApplicationIds: z.array(z.string()),
});
